import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import LocalizedLink from './LocalizedLink';
import { ChevronRight, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const segmentLabels: Record<string, Record<string, string>> = {
  services: { de: 'Services', en: 'Services', tr: 'Hizmetler', fr: 'Services', ru: 'Услуги', it: 'Servizi', es: 'Servicios', ar: 'الخدمات' },
  transfers: { de: 'Transfers', en: 'Transfers', tr: 'Transferler', fr: 'Transferts', ru: 'Трансферы', it: 'Trasferimenti', es: 'Traslados', ar: 'التحويلات' },
  transfer: { de: 'Transfer', en: 'Transfer', tr: 'Transfer', fr: 'Transfert', ru: 'Трансфер', it: 'Trasferimento', es: 'Traslado', ar: 'النقل' },
  tours: { de: 'Touren', en: 'Tours', tr: 'Turlar', fr: 'Excursions', ru: 'Туры', it: 'Tour', es: 'Tours', ar: 'الجولات' },
  fleet: { de: 'Fuhrpark', en: 'Fleet', tr: 'Filo', fr: 'Flotte', ru: 'Автопарк', it: 'Flotta', es: 'Flota', ar: 'الأسطول' },
  booking: { de: 'Buchung', en: 'Booking', tr: 'Rezervasyon', fr: 'Réservation', ru: 'Бронирование', it: 'Prenotazione', es: 'Reserva', ar: 'الحجز' },
  contact: { de: 'Kontakt', en: 'Contact', tr: 'İletişim', fr: 'Contact', ru: 'Контакт', it: 'Contatto', es: 'Contacto', ar: 'اتصل' },
  city: { de: 'Städte', en: 'Cities', tr: 'Şehirler', fr: 'Villes', ru: 'Города', it: 'Città', es: 'Ciudades', ar: 'المدن' },
  rechtliches: { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  datenschutz: { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  agb: { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  impressum: { de: 'Impressum', en: 'Impressum', tr: 'Künye', fr: 'Impressum', ru: 'Выходные данные', it: 'Impressum', es: 'Impressum', ar: 'بيانات النشر' },
  legal: { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  privacy: { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  terms: { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  yasal: { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  gizlilik: { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  'kullanim-kosullari': { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  kunye: { de: 'Künye', en: 'Impressum', tr: 'Künye', fr: 'Impressum', ru: 'Выходные данные', it: 'Impressum', es: 'Impressum', ar: 'بيانات النشر' },
  'mentions-legales': { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  confidentialite: { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  cgu: { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  'pravovaya-informatsiya': { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  'politika-konfidentsialnosti': { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  'usloviya-i-polozheniya': { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  'vykhodnye-dannye': { de: 'Impressum', en: 'Impressum', tr: 'Künye', fr: 'Impressum', ru: 'Выходные данные', it: 'Impressum', es: 'Impressum', ar: 'بيانات النشر' },
  'note-legali': { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  'informativa-privacy': { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  'termini-e-condizioni': { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  'aviso-legal': { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  'politica-de-privacidad': { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  'terminos-y-condiciones': { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  'malumat-qanuniya': { de: 'Rechtliches', en: 'Legal', tr: 'Yasal', fr: 'Mentions légales', ru: 'Правовая информация', it: 'Note legali', es: 'Aviso legal', ar: 'معلومات قانونية' },
  'siyasat-alkhususiya': { de: 'Datenschutz', en: 'Privacy', tr: 'Gizlilik', fr: 'Confidentialité', ru: 'Конфиденциальность', it: 'Privacy', es: 'Privacidad', ar: 'سياسة الخصوصية' },
  'alshurut-walahkam': { de: 'AGB', en: 'Terms', tr: 'Kullanım Koşulları', fr: 'CGU', ru: 'Условия', it: 'Termini', es: 'Términos', ar: 'الشروط والأحكام' },
  'bayanat-alnashr': { de: 'Impressum', en: 'Impressum', tr: 'Künye', fr: 'Impressum', ru: 'Выходные данные', it: 'Impressum', es: 'Impressum', ar: 'بيانات النشر' },
};

const Breadcrumbs = () => {
  const { lang } = useLanguage();
  const location = useLocation();

  // Strip language prefix
  const path = location.pathname.replace(/^\/(de|en|tr|fr|ru|it|es|ar)/, '') || '/';
  if (path === '/') return null;

  const segments = path.split('/').filter(Boolean);
  const crumbs = segments.map((seg, i) => {
    const href = '/' + segments.slice(0, i + 1).join('/');
    const label = segmentLabels[seg]?.[lang] || seg.replace(/^taxi-salzburg-/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return { href, label };
  });

  const allCrumbs = [{ href: '/', label: 'Home' }, ...crumbs];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": allCrumbs.map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": c.label,
      "item": `https://www.taxisalzburg.net/${lang}${c.href === '/' ? '' : c.href}`,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <nav aria-label="Breadcrumb" className="bg-muted/50 border-b border-border">
        <div className="container py-3">
          <ol className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap">
            {allCrumbs.map((crumb, i) => (
              <li key={crumb.href} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
                {i === allCrumbs.length - 1 ? (
                  <span className="text-foreground font-medium truncate max-w-[200px]">
                    {i === 0 && <Home className="w-3.5 h-3.5 inline mr-1" />}
                    {crumb.label}
                  </span>
                ) : (
                  <LocalizedLink
                    to={crumb.href}
                    className="hover:text-primary transition-colors truncate max-w-[200px]"
                  >
                    {i === 0 && <Home className="w-3.5 h-3.5 inline mr-1" />}
                    {crumb.label}
                  </LocalizedLink>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
